import { format, DateFormat, Zone } from "@regal-voice/ui";
import { classifySentiment } from "Components/shared/SentimentDisplay/SentimentDisplay";
import { RecentActivity } from "Types/RecentActivity";

export type ActivityEventConfig = {
    icon: string;
    iconColor: string;
    text: string;
};

export const INLINE_FIELD_SEPARATOR_PIPE = "  |  ";
// Zero-width space used as an invisible marker to identify genuine key:value
// pairs that should receive bold formatting. Only kvField() and explicit
// attribute/custom-event formatters apply this marker — freeform content
// (SMS bodies, transcripts) is never marked.
export const KV_FIELD_MARKER = "\u200B";
const SMS_SNIPPET_MAX_LENGTH = 120;
const SUMMARY_SNIPPET_MAX_LENGTH = 120;

const CONTACT_CREATED_SKIP = new Set([
    "firstName",
    "lastName",
    "first_name",
    "last_name",
    "email",
    "contactPhone",
    "contact_phone",
    "phone",
    "brand",
    "profile_id",
    "external_id",
    "created_at",
    "source",
    "eventType",
    "event_type",
]);

function truncate(value: string, maxLength: number): string {
    if (maxLength <= 0) {
        return "";
    }
    if (value.length <= maxLength) {
        return value;
    }
    if (maxLength === 1) {
        return "…";
    }
    return `${value.slice(0, maxLength - 1)}…`;
}

function compact(fields: (string | null | undefined)[]): string[] {
    return fields.filter((f): f is string => !!f);
}

function formatDuration(seconds: number): string {
    if (!Number.isFinite(seconds)) {
        return "0s";
    }
    const totalSeconds = Math.max(0, Math.floor(seconds));
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatAttrValue(value: unknown): string {
    if (value === null) {
        return "null";
    }
    if (value === "") {
        return '""';
    }
    if (typeof value === "object") {
        return JSON.stringify(value);
    }
    return String(value);
}

function kvField(key: string, value: string | null | undefined): string | null {
    return value != null && value !== "" ? `${KV_FIELD_MARKER}${key}: ${value}` : null;
}

function formatScheduledDate(isoDate: string | null | undefined, timezone: string | null | undefined): string | null {
    if (!isoDate) {
        return null;
    }
    try {
        return format(isoDate, { timezone: (timezone ?? undefined) as Zone | undefined, format: DateFormat.PRETTY_AT });
    } catch {
        return isoDate;
    }
}

type AttributeChange = { attr: string; oldVal: unknown; newVal: unknown };

function extractFirstChange(changes: Record<string, unknown>, path: string[] = []): AttributeChange | null {
    for (const [key, value] of Object.entries(changes)) {
        if (value && typeof value === "object") {
            const obj = value as Record<string, unknown>;
            if ("new_value" in obj || "old_value" in obj) {
                return { attr: [...path, key].join("."), oldVal: obj.old_value, newVal: obj.new_value };
            }
            const nested = extractFirstChange(obj, [...path, key]);
            if (nested) {
                return nested;
            }
        }
    }
    return null;
}

const OUTCOME_MAP: Record<string, string> = {
    Succeed: "completed",
    Fail: "failed",
    "Journey Stopped": "stopped",
};

type InlineFieldsMap = {
    [eventName: string]: (event: RecentActivity) => string | null;
};

const inlineFieldsKeyValueMap: InlineFieldsMap = {
    "call.completed": (e) => {
        const fields = compact([
            kvField("Campaign", e.properties?.campaign_name) ?? e.properties?.type ?? null,
            kvField("Disposition", e.properties?.disposition),
            e.properties?.handle_time != null
                ? kvField("Duration", formatDuration(Number(e.properties.handle_time)))
                : null,
            kvField("Agent", e.properties?.agent_fullname),
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "call.placed": (e) => kvField("Agent", e.properties?.agent_fullname),
    "call.ivr.triggered": (e) => {
        const fields = compact([
            kvField("Flow", e.properties?.ivr_flow_name),
            kvField("Regal Voice Phone", e.properties?.regal_voice_phone_internal_name),
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "call.recording.available": (e) => {
        const talkTime = e.properties?.talk_time ?? e.properties?.talkTime;
        return talkTime != null ? kvField("Duration", formatDuration(Number(talkTime))) : null;
    },
    "call.transcript.available": (e) => {
        const snippet = e.properties?.call_summary ?? e.properties?.transcript;
        return snippet ? truncate(String(snippet), SUMMARY_SNIPPET_MAX_LENGTH) : null;
    },
    "task.created": (e) => {
        const taskLabel =
            kvField("Campaign", e.properties?.campaign_name) ??
            e.properties?.custom_task_name ??
            kvField("Type", e.properties?.type) ??
            null;
        const fields = compact([taskLabel, kvField("Queue", e.properties?.queue)]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "task.canceled": (e) => {
        const taskLabel =
            kvField("Campaign", e.properties?.campaign_name) ??
            e.properties?.custom_task_name ??
            kvField("Type", e.properties?.type) ??
            null;
        const fields = compact([
            taskLabel,
            kvField("Canceled By", e.properties?.canceled_by),
            kvField("Reason", e.properties?.cancel_reason),
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "task.reservation.created": (e) => {
        const taskLabel =
            kvField("Campaign", e.properties?.campaign_name) ??
            e.properties?.custom_task_name ??
            kvField("Type", e.properties?.type) ??
            null;
        const fields = compact([
            taskLabel,
            kvField("Queue", e.properties?.queue),
            kvField("Agent", e.properties?.reserved_agent_fullname),
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "task.reservation.accepted": (e) => {
        const taskLabel =
            kvField("Campaign", e.properties?.campaign_name) ??
            e.properties?.custom_task_name ??
            kvField("Type", e.properties?.type) ??
            null;
        const fields = compact([
            taskLabel,
            kvField("Queue", e.properties?.queue),
            kvField("Agent", e.properties?.reserved_agent_fullname),
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "custom.task.completed": (e) => {
        const fields = compact([
            kvField("Task", e.properties?.custom_task_name),
            kvField("Disposition", e.properties?.disposition),
            kvField("Agent", e.properties?.agent_fullname),
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "sms.sent": (e) => (e.properties?.content ? truncate(String(e.properties.content), SMS_SNIPPET_MAX_LENGTH) : null),
    "sms.received": (e) => {
        const content = e.properties?.content;
        return content ? truncate(String(content), SMS_SNIPPET_MAX_LENGTH) : null;
    },
    "sms.queued": (e) => kvField("Campaign", e.properties?.campaign_name),
    "sms.failed": (e) =>
        kvField("Campaign", e.properties?.campaign_name) ?? kvField("Agent", e.properties?.agent_fullname),
    "sms.undelivered": (e) => {
        const fields = compact([
            kvField("Campaign", e.properties?.campaign_name) ?? kvField("Agent", e.properties?.agent_fullname),
            e.properties?.error_code != null ? kvField("Error Code", String(e.properties.error_code)) : null,
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "sms.conversation.completed": (e) => {
        const fields = compact([
            kvField("Disposition", e.properties?.disposition),
            kvField("Agent", e.properties?.agent_fullname),
            kvField("Notes", e.properties?.notes),
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "mms.sent": (e) => (e.properties?.content ? truncate(String(e.properties.content), SMS_SNIPPET_MAX_LENGTH) : null),
    "email.sent": (e) => {
        const fields = compact([
            kvField("From", e.properties?.sender_email),
            kvField("Subject", e.properties?.subject),
            e.properties?.body_snippet ? truncate(String(e.properties.body_snippet), SUMMARY_SNIPPET_MAX_LENGTH) : null,
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "email.received": (e) => {
        const fields = compact([
            kvField("Subject", e.properties?.subject),
            e.properties?.body_snippet ? truncate(String(e.properties.body_snippet), SUMMARY_SNIPPET_MAX_LENGTH) : null,
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "email.conversation.completed": (e) => {
        const fields = compact([
            kvField("Disposition", e.properties?.disposition),
            kvField("Agent", e.properties?.agent_fullname),
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "voicemail.recording.available": (e) => {
        const talkTime = e.properties?.talk_time ?? e.properties?.talkTime;
        return talkTime != null ? kvField("Duration", formatDuration(Number(talkTime))) : null;
    },
    "voicemail.transcript.available": (e) => {
        const transcript = e.properties?.transcript;
        return transcript ? truncate(String(transcript), SUMMARY_SNIPPET_MAX_LENGTH) : null;
    },
    "voicemail.completed": (e) => {
        const fields = compact([
            kvField("Agent", e.properties?.agent_name ?? e.properties?.agentFullname ?? e.agentFullname),
            kvField("Disposition", e.properties?.disposition),
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "contact.created": (e) => {
        const firstName = e.properties?.firstName ?? e.firstName;
        const lastName = e.properties?.lastName ?? e.lastName;
        const name = compact([firstName, lastName]).join(" ");
        const extraFields = Object.entries((e.properties ?? {}) as Record<string, unknown>)
            .filter(([k, v]) => !CONTACT_CREATED_SKIP.has(k) && v != null && v !== "")
            .slice(0, 3)
            .map(([k, v]) => `${KV_FIELD_MARKER}${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`);
        return compact([name, ...extraFields]).join(INLINE_FIELD_SEPARATOR_PIPE) || null;
    },
    "contact.phone.created": (e) =>
        kvField(
            "Phone",
            e.properties?.new_phone_properties?.phone ?? e.properties?.contactPhone ?? e.properties?.contact_phone
        ),
    "contact.email.created": (e) => kvField("Email", e.properties?.new_email_properties?.email ?? e.properties?.email),
    "contact.attribute.edited": (e) => {
        // Prefer nested changes structure (e.g. properties.changes.custom_properties.address.city)
        if (e.properties?.changes && typeof e.properties.changes === "object") {
            const change = extractFirstChange(e.properties.changes as Record<string, unknown>);
            if (change) {
                const { attr, oldVal, newVal } = change;
                const leafAttr = attr.split(".").pop() ?? attr;
                if (oldVal !== undefined && newVal !== undefined) {
                    return `${KV_FIELD_MARKER}${leafAttr}: ${formatAttrValue(oldVal)} → ${formatAttrValue(newVal)}`;
                }
                if (newVal !== undefined) {
                    return `${KV_FIELD_MARKER}${leafAttr}: ${formatAttrValue(newVal)}`;
                }
                if (oldVal !== undefined) {
                    return `${KV_FIELD_MARKER}${leafAttr}: ${formatAttrValue(oldVal)} removed`;
                }
                return leafAttr;
            }
        }
        // Fall back to flat attribute structure
        const attr = e.properties?.attribute_name ?? e.properties?.attributeName;
        const oldVal = e.properties?.old_value ?? e.properties?.oldValue;
        const newVal = e.properties?.new_value ?? e.properties?.newValue;
        if (!attr) {
            return null;
        }
        if (oldVal != null || newVal != null) {
            return `${KV_FIELD_MARKER}${attr}: ${formatAttrValue(oldVal)} → ${formatAttrValue(newVal)}`;
        }
        return String(attr);
    },
    "contact.subscribed": (e) => kvField("Channel", e.properties?.channel),
    "contact.unsubscribed": (e) => {
        const fields = compact([kvField("Channel", e.properties?.channel), kvField("Reason", e.properties?.reason)]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "contact.experiment.assigned": (e) => {
        const fields = compact([
            kvField("Journey", e.properties?.journey_name),
            kvField("Experiment", e.properties?.experiment_name ?? e.properties?.experimentName),
            kvField("Variant", e.properties?.experiment_variant ?? e.properties?.variant_name ?? e.properties?.variant),
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "contacted.after.hours": (e) => kvField("Channel", e.properties?.channel),
    "cancel.all.automated.tasks": (e) => kvField("Canceled By", e.properties?.canceled_by),
    "scheduled.callback.requested": (e) => {
        const dateDisplay =
            e.properties?.callback_friendly_timestamp ||
            formatScheduledDate(e.properties?.isoCallbackDate, e.properties?.timezone);
        const fields = compact([
            dateDisplay ? kvField("Scheduled", dateDisplay) : null,
            kvField(
                "Agent",
                e.properties?.scheduling_agent_fullname ?? e.properties?.agentFullname ?? e.properties?.agent_name
            ),
            kvField("Notes", e.properties?.notes_from_agent),
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "scheduled.reminder.created": (e) => {
        const scheduledDate = formatScheduledDate(
            e.properties?.scheduled_at ?? e.properties?.scheduledAt,
            e.properties?.timezone
        );
        const fields = compact([
            scheduledDate ? kvField("Scheduled", scheduledDate) : null,
            kvField(
                "Agent",
                e.properties?.scheduling_agent_fullname ?? e.properties?.schedulingAgentName ?? e.properties?.agent_name
            ),
            kvField("Notes", e.properties?.notes_from_agent),
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "object.created": (e) => {
        const fields = compact([
            kvField(
                "Type",
                e.properties?.related_object_type_name ?? e.properties?.object_type ?? e.properties?.objectType
            ),
            kvField("Source", e.properties?.related_object_type_source),
        ]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "object.attribute.edited": (e) => {
        const objectType =
            e.properties?.related_object_type_name ?? e.properties?.object_type ?? e.properties?.objectType;
        const attr = e.properties?.attribute_name ?? e.properties?.attributeName;
        const oldVal = e.properties?.old_value ?? e.properties?.oldValue;
        const newVal = e.properties?.new_value ?? e.properties?.newValue;
        if (!objectType) {
            return null;
        }
        if (attr && (oldVal != null || newVal != null)) {
            return `${objectType}${INLINE_FIELD_SEPARATOR_PIPE}${KV_FIELD_MARKER}${attr}: ${formatAttrValue(oldVal)} → ${formatAttrValue(newVal)}`;
        }
        return compact([objectType, attr]).join(INLINE_FIELD_SEPARATOR_PIPE) || null;
    },
    "call.analysis.available": (e) => {
        const analysis = e.properties?.call_analysis;
        const analysisSummary =
            analysis && typeof analysis === "object"
                ? Object.entries(analysis as Record<string, unknown>)
                      .slice(0, 3)
                      .filter(([, v]) => v != null && v !== "")
                      .map(([k, v]) => `${KV_FIELD_MARKER}${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
                      .join(INLINE_FIELD_SEPARATOR_PIPE) || null
                : null;
        const fields = compact([kvField("Agent", e.properties?.agent_fullname), analysisSummary]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
    "journey.entered": (e) => kvField("Journey", e.properties?.journey_name ?? e.journey_name),
    "journey.exited": (e) => {
        const nodeName = e.properties?.node_name ?? e.node_name;
        const outcome = nodeName ? (OUTCOME_MAP[nodeName] ?? nodeName) : null;
        const fields = compact([kvField("Journey", e.properties?.journey_name ?? e.journey_name), outcome]);
        return fields.length ? fields.join(INLINE_FIELD_SEPARATOR_PIPE) : null;
    },
};

export type EnrichedEventsVariant = "" | "off" | "pipes_key_value_pair";

export type EntityId = {
    id: string;
    label: string;
};

export function getEntityId(event: RecentActivity): EntityId | null {
    // Task ID takes priority for any event that carries one — covers call.*, task.*,
    // and any future events without needing to maintain an explicit allowlist.
    // call_sid/call_id are aliases for task_sid/task_id on call events.
    const taskId =
        event.properties?.task_sid ??
        event.properties?.task_id ??
        event.properties?.call_sid ??
        event.properties?.call_id;
    if (taskId) {
        return { id: String(taskId), label: "Task ID" };
    }
    if (event.name.startsWith("contact.")) {
        const id = event.properties?.profile_id ?? event.profile_id;
        return id ? { id: String(id), label: "Profile ID" } : null;
    }
    if (event.name.startsWith("object.")) {
        const id = event.properties?.object_id ?? event.properties?.related_object_id ?? event.properties?.objectId;
        return id ? { id: String(id), label: "Object ID" } : null;
    }
    if (event.name.startsWith("sms.") || event.name.startsWith("mms.")) {
        const id = event.properties?.sms_message_id;
        return id ? { id: String(id), label: "Message ID" } : null;
    }
    return null;
}

const CUSTOM_EVENT_PROPERTIES_SKIP = new Set([
    "brand",
    "contact_phone",
    "contact_email",
    "email",
    "phone",
    "created_at",
    "profile_id",
    "external_id",
]);

function getCustomEventInlineFields(event: RecentActivity): string | null {
    const props = event.properties;
    if (!props || typeof props !== "object") {
        return null;
    }
    const summary = Object.entries(props as Record<string, unknown>)
        .filter(([k, v]) => !CUSTOM_EVENT_PROPERTIES_SKIP.has(k) && v != null && v !== "")
        .slice(0, 3)
        .map(([k, v]) => `${KV_FIELD_MARKER}${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
        .join(INLINE_FIELD_SEPARATOR_PIPE);
    return summary || null;
}

const REGAL_EVENT_PREFIXES = [
    "call.",
    "task.",
    "custom.task.",
    "sms.",
    "mms.",
    "email.",
    "voicemail.",
    "contact.",
    "contacted.",
    "cancel.",
    "scheduled.",
    "object.",
    "journey.",
    "agent.",
    "pageview",
];

function isRegalEvent(name: string): boolean {
    return REGAL_EVENT_PREFIXES.some((prefix) => name === prefix || name.startsWith(prefix));
}

export type SentimentResult = {
    label: "Positive" | "Negative" | "Neutral";
    percentage: number;
    color: "positive" | "negative" | "neutral";
};

export function getCallSentiment(event: RecentActivity): SentimentResult | null {
    if (event.name !== "call.transcript.available") {
        return null;
    }
    const sentiments = event.properties?.sentiments as Record<string, number | null> | null | undefined;
    const raw = sentiments?.contact_sentiment ?? sentiments?.contactSentiment;
    if (raw == null) {
        return null;
    }
    const score = Number(raw);
    if (!Number.isFinite(score)) {
        return null;
    }
    const { identifier, label } = classifySentiment(score);
    return { label, percentage: Math.round(score), color: identifier };
}

export function getInlineFieldsForVariant(event: RecentActivity, variant: EnrichedEventsVariant): string | null {
    if (!variant || variant === "off") {
        return null;
    }
    let result: string | null;
    const handler = inlineFieldsKeyValueMap[event.name];
    if (!handler) {
        if (isRegalEvent(event.name)) {
            result = null;
        } else {
            try {
                result = getCustomEventInlineFields(event);
            } catch {
                result = null;
            }
        }
    } else {
        try {
            result = handler(event);
        } catch {
            result = null;
        }
    }
    return result;
}

type ActivityEventConfigMap = {
    icon: (recentActivity: RecentActivity) => string;
    iconColor: (recentActivity: RecentActivity) => string;
    text: (recentActivity: RecentActivity) => string;
};

const defaultMapping: ActivityEventConfigMap = {
    icon: () => "list",
    iconColor: (recentActivity: RecentActivity) => (recentActivity.source === "Regal Voice" ? "purple-3" : "gray-4"),
    text: (recentActivity: RecentActivity) => recentActivity.name,
};

const directionIconMapping = (recentActivity: RecentActivity) => {
    return `${recentActivity.properties?.direction === "OUTBOUND" ? "outbound" : "inbound"}-message`;
};

const EventTypeMapping: { [id: string]: Partial<ActivityEventConfigMap> } = {
    pageview: {
        icon: () => "pageview",
        iconColor: () => "gray-4",
        text: (recentActivity: RecentActivity) => "Pageview" + (recentActivity.page ? ": " + recentActivity.page : ""),
    },
    "contact.subscribed": {
        icon: () => "list",
        iconColor: () => "purple-3",
    },
    "contact.unsubscribed": {
        icon: () => "list",
        iconColor: () => "purple-3",
    },
    "Outbound SMS Sent": {
        icon: () => "outbound-message",
        iconColor: () => "blue-4",
    },
    "Outbound SMS Queued": {
        icon: () => "outbound-message",
        iconColor: () => "blue-5",
    },
    "sms.received": {
        icon: (recentActivity) => directionIconMapping(recentActivity),
        iconColor: () => "blue-3",
    },
    "mms.received": {
        icon: (recentActivity) => directionIconMapping(recentActivity),
        iconColor: () => "blue-3",
    },
    "sms.queued": {
        icon: (recentActivity) => directionIconMapping(recentActivity),
        iconColor: () => "blue-3",
    },
    "sms.sent": {
        icon: (recentActivity) => directionIconMapping(recentActivity),
        iconColor: () => "blue-3",
    },
    "mms.sent": {
        icon: (recentActivity) => directionIconMapping(recentActivity),
        iconColor: () => "blue-3",
    },
    "sms.conversation.completed": {
        icon: () => "task-completed",
        iconColor: () => "purple-3",
    },
    "call.completed": {
        icon: () => "task-completed",
        iconColor: () => "purple-3",
    },
    "Outbound Call": {
        iconColor: () => "green-3",
    },
    "Inbound Call": {
        iconColor: () => "gray-4",
    },
    "Missed Outbound Call": {
        iconColor: () => "red-3",
    },
    "Missed Inbound Call": {
        iconColor: () => "red-4",
    },
};

export function getEventTypeViewConfig(recentActivity: RecentActivity): ActivityEventConfig {
    const mapping = EventTypeMapping[recentActivity.name];

    return {
        ...defaultMapping,
        ...mapping,
        iconColor: (mapping?.iconColor ?? defaultMapping.iconColor)(recentActivity),
        icon: (mapping?.icon ?? defaultMapping.icon)(recentActivity),
        text: (mapping?.text ?? defaultMapping.text)(recentActivity),
    };
}
